import { ApiService } from 'js/src/client';
import { ErrorEnum, MaxScenes, type VideoStatus } from 'js/src/client';
import { isValidErrorCode } from 'js/src/data/app';
import { LOCKED_FEATURES } from 'js/src/data/modals';
import { HTTP_STATUS_CODES } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import UserPrintableError from 'js/src/libs/user-printable-error';
import { DownloadHttpError, DownloadLockedError } from 'js/src/libs/video-downloader';
import { isApiError, isApiErrorWithProperty } from 'js/src/types';

export type videoDownloadResponse = {
    videoStatus: VideoStatus;
    taskId: string | null;
    url: string | null;
};

export const mapCustomColorAndFontsErrorsToUpsellModalKey = {
    [ErrorEnum.CUSTOM_COLORS_AND_FONTS_NOT_ALLOWED]:
        LOCKED_FEATURES.CANT_PUBLISH_VIDEO_CUSTOM_COLOR_AND_FONT,
    [ErrorEnum.CUSTOM_COLORS_NOT_ALLOWED]: LOCKED_FEATURES.CANT_PUBLISH_VIDEO_CUSTOM_COLOR,
    [ErrorEnum.CUSTOM_FONTS_NOT_ALLOWED]: LOCKED_FEATURES.CANT_PUBLISH_VIDEO_CUSTOM_FONT,
};

export function videoDownload(video_id: number): Promise<videoDownloadResponse> {
    return new Promise((resolve, reject) => {
        ApiService.videoShareDownloadRetrieve({
            path: { video_id: video_id.toString() },
        })
            .then((response) => {
                const { video_status, task_id, url } = response.data;
                resolve({
                    videoStatus: video_status,
                    taskId: task_id,
                    url,
                });
            })
            .catch((err) => {
                if (
                    isApiError(err) &&
                    (err.status === (HTTP_STATUS_CODES.FORBIDDEN as number) ||
                        err.status === (HTTP_STATUS_CODES.NOT_FOUND as number))
                ) {
                    reject(new DownloadHttpError(err.status));
                } else if (
                    isApiErrorWithProperty(err, 'error_code') &&
                    isValidErrorCode(err.body.error_code)
                ) {
                    const errorCode = err.body.error_code;
                    if (
                        errorCode === ErrorEnum.PREMIUM_MEDIA_CREDITS ||
                        errorCode === ErrorEnum.HIT_MEDIA_CREDIT_LIMIT ||
                        errorCode === ErrorEnum.UPGRADE_FOR_UNLIMITED_MEDIA ||
                        errorCode === ErrorEnum.UPGRADE_FOR_AI_AVATAR ||
                        errorCode === ErrorEnum.VIDEO_DURATION_TOO_LONG
                    ) {
                        reject(new DownloadLockedError(errorCode));
                    } else if (errorCode === ErrorEnum.VIDEO_HAS_TOO_MANY_SCENES) {
                        const message = `To download your video, please reduce the amount of scenes to ${MaxScenes.LIMIT} or less by removing some scenes.`;
                        reject(new UserPrintableError(message));
                    } else if (errorCode === ErrorEnum.ALREADY_PUBLISHED) {
                        const message = 'This video is already published.';
                        reject(new UserPrintableError(message));
                    } else if (errorCode === ErrorEnum.TRANSITIONED_MEDIA_PRESENT) {
                        analytics.track('User encountered archived assets', {
                            user_id: SERVER.USER.id,
                            email: SERVER.USER.email,
                            error_details: err.body.details,
                        });
                        const message =
                            'Some of the media in your video is no longer available from our content providers. Please edit your video to choose new media.';
                        reject(new UserPrintableError(message));
                    } else if (
                        errorCode === ErrorEnum.UNABLE_TO_FIND_VIDEO ||
                        errorCode === ErrorEnum.UNABLE_TO_READ_VIDEO ||
                        errorCode === ErrorEnum.NO_URL_AVAILABLE ||
                        errorCode === ErrorEnum.UNKNOWN_STATE
                    ) {
                        analytics.track('User encountered error', {
                            user_id: SERVER.USER.id,
                            email: SERVER.USER.email,
                            error_details: errorCode,
                        });
                        reject(new UserPrintableError(errorCode));
                    } else if (
                        mapCustomColorAndFontsErrorsToUpsellModalKey[
                            err.body
                                .error as keyof typeof mapCustomColorAndFontsErrorsToUpsellModalKey
                        ]
                    ) {
                        reject(new DownloadLockedError(errorCode));
                    } else {
                        ErrorReporter.capture(err);
                        reject(new Error('unknown error'));
                    }
                } else {
                    ErrorReporter.capture(err);
                    reject(new Error('unknown error'));
                }
            });
    });
}
export async function trackVideoDownload(video_id: number): Promise<void> {
    // handle the tracking first because the UI should already have the download url
    analytics.track('Downloaded Video', {
        video_id: video_id,
        plan: SERVER.USER.plan,
    });
    // Track the activation event (downloaded video)
    if (SERVER.ANALYTICS && SERVER.ANALYTICS.downloaded_video_count === 0) {
        // send facebook event
        try {
            fbq('track', 'Activated', {
                channel: 'download',
            });
        } catch (e) {
            // ignore
        }

        // send google event
        try {
            gtag('event', 'activated', {
                send_to: 'AW-835353444/JkHKCJb86ZMBEOT2qY4D',
                channel: 'download',
            });
        } catch (e) {
            // ignore
        }

        // send the 2nd google event
        try {
            gtag('event', 'conversion', {
                send_to: 'AW-372587568/bmXJCPHrhpICELD41LEB',
            });
        } catch (e) {
            // ignore
        }
    }

    await ApiService.videoShareDownloadCreate({
        path: { video_id: video_id.toString() },
    });
}

export function getDownloadLink(gcs_url: string): string {
    /*
     * We had issues with Windows Defender/SmartScreen blocking
     * downloads from the Google Cloud Storage bucket directly,
     * so proxy the downloads through the Lumen5 domain
     * (dl.lumen5.com).
     *
     * The proxy only works for the lumen5-prod-renderedvideos bucket,
     * and trims out the bucket name from the url. Example:
     *
     * GCS URL: https://lumen5-prod-renderedvideos.storage.googleapis.com/my-video.mp4
     * lumen5 domain URL: https://dl.lumen5.com/my-video.mp4
     */
    return gcs_url.replace('lumen5-prod-renderedvideos.storage.googleapis.com', 'dl.lumen5.com');
}

export function downloadStringAsFile(data: string, fileName: string) {
    const element = document.createElement('a');
    element.setAttribute('href', data);
    element.setAttribute('download', fileName);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
}

/**
 * Downloads a file from the given URL and returns a File object.
 *
 * @param url       The file URL you want to download.
 * @param fileName  The name (and extension) to give to the File object.
 */
export async function downloadUrlAsFile({
    url,
    fileName = undefined,
}: {
    url: string;
    fileName?: string;
}): Promise<File> {
    // If no file name is provided, extract it from the URL
    if (!fileName) {
        const parsedUrl = new URL(url);
        const pathSegments = parsedUrl.pathname.split('/');
        fileName = pathSegments.pop() || 'downloaded-file';
    }

    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to download file from ${url}. Status: ${response.status}`);
    }

    // Convert the response to a Blob
    const blob = await response.blob();

    // Create a File object from the Blob
    return new File([blob], fileName, { type: blob.type });
}
